<template>
  <!-- Mobile trigger -->
<button
    class="tfp-mobile-trigger lg:hidden flex items-center gap-2 px-4 py-2 mb-4 ml-6 text-sm font-medium rounded-lg"
    style="background: var(--tfp-header-bg); color: #ffffff; margin-top: 0.75rem;"
    @click="openDrawer"
>
    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 4h18M7 8h10M11 12h4"/>
    </svg>
    Filter Tours
    <span v-if="hasActiveFilters" class="ml-1 w-2 h-2 rounded-full bg-white"></span>
</button>

  <!-- Mobile overlay -->
  <Transition name="tfp-fade">
    <div
      v-if="drawerOpen"
      class="lg:hidden fixed inset-0 z-40"
      style="background: rgba(0,0,0,0.5);"
      @click="closeDrawer"
    ></div>
  </Transition>

  <!-- Panel: fixed drawer on mobile, sticky sidebar on desktop -->
<aside
    :class="[
    'tfp-panel fixed top-0 left-0 ml-0 lg:ml-1 h-full w-80 z-50 overflow-y-auto',
    'transition-transform duration-300 ease-in-out',
    'lg:block lg:relative lg:left-auto lg:w-64 lg:z-auto',
    'lg:transition-none lg:sticky lg:top-6 lg:mt-4 lg:self-start',
    drawerOpen ? 'translate-x-0' : 'hidden lg:block'
]"
    :style="{
        background: 'var(--color-bg-panel, #ffffff)',
        maxHeight: '100vh',
        overflowY: 'auto',
    }"
>
    <!-- Panel header -->
    <div class="tfp-panel-header">
        <div class="flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 4h18M7 8h10M11 12h4"/>
            </svg>
            <span class="font-semibold tracking-widest text-sm uppercase">Filter Tours</span>
        </div>
        <div class="flex items-center gap-3">
            <button v-if="hasActiveFilters" class="tfp-clear-all" @click="clearAll">Clear all</button>
            <button class="lg:hidden p-1" @click="closeDrawer" aria-label="Close filters">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
            </button>
        </div>
    </div>
    
    <!-- Sort By lives in the results bar on the search page (1_search.vue); this panel still applies it (setSort) -->

    <!-- ── Search ──────────────────────────────────────────────────────── -->
    <div class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.search }">
      <button class="tfp-section__header" @click="toggleSection('search')">
        <span>Search</span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div v-if="searchInput" class="tfp-summary">
        {{ searchInput }}
        <button class="tfp-clear-chip" @click.stop="clearSection('search')">✕</button>
      </div>
      <div v-show="sectionsOpen.search" class="tfp-section__body">
        <input
          class="tfp-text-input"
          type="text"
          placeholder="Search tours…"
          :value="searchInput"
          @input="onSearchInput"
        />
      </div>
    </div>

    <!-- ── Brand (agents) ──────────────────────────────────────────────── -->
    <div v-if="showBrandFilter" class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.brands }">
      <button class="tfp-section__header" @click="toggleSection('brands')">
        <span>Brand</span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div v-if="brandSummary" class="tfp-summary">
        {{ brandSummary }}
        <button class="tfp-clear-chip" @click.stop="clearSection('brands')">✕</button>
      </div>
      <div v-show="sectionsOpen.brands" class="tfp-section__body">
        <div class="tfp-brands">
          <label v-for="opt in brandOptions" :key="opt.value" class="tfp-brand" :class="{ 'is-selected': brands.includes(opt.value) }">
            <input type="checkbox" :value="opt.value" v-model="brands" class="tfp-brand-input" />
            <img v-if="opt.logo" class="tfp-brand-mark" :src="opt.logo" alt="" />
            <span v-else class="tfp-brand-mark is-initials" aria-hidden="true">{{ opt.initials }}</span>
            <span class="tfp-brand-text">
              <span class="tfp-brand-name">{{ opt.label }}</span>
              <span class="tfp-brand-count">{{ opt.count }} {{ opt.count == 1 ? 'tour' : 'tours' }}</span>
            </span>
            <span class="tfp-brand-check" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            </span>
          </label>
        </div>
      </div>
    </div>

    <!-- ── Departing From ──────────────────────────────────────────────── -->
    <div class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.departingFrom }">
      <button class="tfp-section__header" @click="toggleSection('departingFrom')">
        <span>Departing From</span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div v-if="departingFrom" class="tfp-summary">
        {{ departingFrom }}
        <button class="tfp-clear-chip" @click.stop="clearSection('departingFrom')">✕</button>
      </div>
      <div v-show="sectionsOpen.departingFrom" class="tfp-section__body space-y-2">
        <label v-for="city in departureCities" :key="city" class="tfp-checkbox-label">
            <input type="radio" name="departingFrom" :value="city" v-model="departingFrom" class="tfp-checkbox" />
            {{ city }}
        </label>
    </div>
    </div>

    <!-- ── Tour Length ─────────────────────────────────────────────────── -->
    <div class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.duration }">
      <button class="tfp-section__header" @click="toggleSection('duration')">
        <span>Tour Length</span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div v-if="durationSummary" class="tfp-summary">
        {{ durationSummary }}
        <button class="tfp-clear-chip" @click.stop="clearSection('duration')">✕</button>
      </div>
      <div v-show="sectionsOpen.duration" class="tfp-section__body">
        <DualRangeSlider
          :min="durationBounds.min"
          :max="durationBounds.max"
          v-model:modelMin="durationMin"
          v-model:modelMax="durationMax"
          :values="durationValues"
          suffix=" days"
          singular-suffix=" day"
        />
      </div>
    </div>

    <!-- ── Price Range ─────────────────────────────────────────────────── -->
    <div class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.price }">
      <button class="tfp-section__header" @click="toggleSection('price')">
        <span>Price Range</span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div v-if="priceSummary" class="tfp-summary">
        {{ priceSummary }}
        <button class="tfp-clear-chip" @click.stop="clearSection('price')">✕</button>
      </div>
      <div v-show="sectionsOpen.price" class="tfp-section__body">
        <DualRangeSlider
          :min="priceBounds.min"
          :max="priceBounds.max"
          v-model:modelMin="priceMin"
          v-model:modelMax="priceMax"
          :values="priceValues"
          :prefix="currencySymbol"
        />
      </div>
    </div>

    <!-- ── Travel Dates ────────────────────────────────────────────────── -->
    <div class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.dates }">
      <button class="tfp-section__header" @click="toggleSection('dates')">
        <span>Travel Dates</span>
        <span v-if="dateLoading" class="tfp-loading-dot"></span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <!-- previous/next day: moves the selected start (and end) date by a day -->
      <div v-if="dateSummary" class="tfp-summary">
        <span v-if="dateFrom" class="tfp-date-stepper">
          <button type="button" class="tfp-step-btn" aria-label="Previous day" :disabled="!canStepDatesBack" @click.stop="stepDates(-1)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <span class="tfp-date-label">{{ dateSummary }}</span>
          <button type="button" class="tfp-step-btn" aria-label="Next day" @click.stop="stepDates(1)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        </span>
        <template v-else>{{ dateSummary }}</template>
        <button class="tfp-clear-chip" @click.stop="clearSection('dates')">✕</button>
      </div>
      <div v-show="sectionsOpen.dates" class="tfp-section__body space-y-3">
        <div class="tfp-date-mode" role="radiogroup" aria-label="Date search">
          <button type="button" role="radio" :aria-checked="dateMode === 'single'" :class="{ 'is-active': dateMode === 'single' }" @click="setDateMode('single')">Single day</button>
          <button type="button" role="radio" :aria-checked="dateMode === 'range'" :class="{ 'is-active': dateMode === 'range' }" @click="setDateMode('range')">Date range</button>
        </div>

        <div v-if="dateMode === 'single'">
          <label class="tfp-label" for="tfp-date-single">Travel date</label>
          <div class="tfp-date-field">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
            <input
              id="tfp-date-single"
              class="tfp-date-input"
              type="date"
              v-model="dateFrom"
              :class="{ 'is-empty': !dateFrom }"
              :min="dateFromMin"
              :max="dateFromMax"
              @click="openPicker"
            />
          </div>
          <div class="tfp-quick-dates">
            <button type="button" :class="{ 'is-active': dateFrom === dateFromMin }" @click="pickDay(0)">Today</button>
            <button type="button" :class="{ 'is-active': dateFrom === dateTomorrow }" @click="pickDay(1)">Tomorrow</button>
          </div>
        </div>

        <template v-else>
          <div>
            <label class="tfp-label" for="tfp-date-from">From</label>
            <div class="tfp-date-field">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
              <input
                id="tfp-date-from"
                class="tfp-date-input"
                type="date"
                v-model="dateFrom"
                :class="{ 'is-empty': !dateFrom }"
                :min="dateFromMin"
                :max="dateFromMax"
                @click="openPicker"
              />
            </div>
          </div>
          <div>
            <label class="tfp-label" for="tfp-date-to">To</label>
            <div class="tfp-date-field">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
              <input
                id="tfp-date-to"
                class="tfp-date-input"
                type="date"
                v-model="dateTo"
                :class="{ 'is-empty': !dateTo }"
                :min="dateToMin"
                @click="openPicker"
              />
            </div>
            <p class="tfp-hint">Leave empty for any date from the start date onwards.</p>
          </div>
        </template>
      </div>
    </div>

    <!-- ── Accommodation ───────────────────────────────────────────────── -->
    <div class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.accommodation }">
      <button class="tfp-section__header" @click="toggleSection('accommodation')">
        <span>Accommodation</span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div v-if="accommodationSummary" class="tfp-summary">
        {{ accommodationSummary }}
        <button class="tfp-clear-chip" @click.stop="clearSection('accommodation')">✕</button>
      </div>
      <div v-show="sectionsOpen.accommodation" class="tfp-section__body space-y-2">
        <label class="tfp-checkbox-label flex-col items-start">
            <div class="flex items-center gap-2">
                <input type="checkbox" value="" v-model="accommodation" class="tfp-checkbox" />
                Day Tours
                <button class="tfp-info-icon-btn" @click.prevent="toggleRoomInfo('__dayTour__')">
                    <svg class="tfp-info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path stroke-linecap="round" d="M12 16v-4M12 8h.01"/>
                    </svg>
                </button>
            </div>
            <p v-if="openRoomInfo === '__dayTour__'" class="tfp-room-desc">
                Tours that return on the same day with no overnight stay.
            </p>
        </label>
        <label v-for="opt in accommodationOptions" :key="opt" class="tfp-checkbox-label flex-col items-start">
          <div class="flex items-center gap-2">
              <input type="checkbox" :value="opt" v-model="accommodation" class="tfp-checkbox" />
              {{ opt }}
              <button 
                  v-if="getRoomDescription(opt)"
                  class="tfp-info-icon-btn"
                  @click.prevent="toggleRoomInfo(opt)"
              >
                  <svg class="tfp-info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path stroke-linecap="round" d="M12 16v-4M12 8h.01"/>
                  </svg>
              </button>
          </div>
          <p v-if="openRoomInfo === opt" class="tfp-room-desc">{{ getRoomDescription(opt) }}</p>
      </label>
    </div>
    </div>

    <!-- ── Group Size ──────────────────────────────────────────────────── -->
    <div v-if="false" class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.groupSize }">
      <button class="tfp-section__header" @click="toggleSection('groupSize')">
        <span>Group Size</span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div v-if="groupSizeSummary" class="tfp-summary">
        {{ groupSizeSummary }}
        <button class="tfp-clear-chip" @click.stop="clearSection('groupSize')">✕</button>
      </div>
      <div v-show="sectionsOpen.groupSize" class="tfp-section__body space-y-2">
        <label v-for="opt in [{ value: '', label: 'Any size' }, { value: '16', label: 'Max 16' }, { value: '34', label: 'Max 34' }]" :key="opt.value" class="tfp-checkbox-label">
          <input type="radio" name="groupSize" :value="opt.value" v-model="groupSize" class="tfp-checkbox" />
          {{ opt.label }}
        </label>
      </div>
    </div>

    <!-- ── Destination ─────────────────────────────────────────────────── -->
    <div v-if="false" class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.destinations }">
      <button class="tfp-section__header" @click="toggleSection('destinations')">
        <span>Destination</span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div v-if="destinations.length" class="tfp-summary">
        {{ destinations.join(', ') }}
        <button class="tfp-clear-chip" @click.stop="clearSection('destinations')">✕</button>
      </div>
      <div v-show="sectionsOpen.destinations" class="tfp-section__body">
        <p v-if="!destinationOptions.length" class="text-xs" style="color: var(--color-text-muted, #9ca3af);">
          No options available yet.
        </p>
        <div v-else class="space-y-2">
          <label v-for="opt in destinationOptions" :key="opt" class="tfp-checkbox-label">
            <input type="checkbox" :value="opt" v-model="destinations" class="tfp-checkbox" />
            {{ opt }}
          </label>
        </div>
      </div>
    </div>

    <!-- ── Highlights ──────────────────────────────────────────────────── -->
    <div v-if="false" class="tfp-section" :class="{ 'tfp-section--open': sectionsOpen.highlights }">
      <button class="tfp-section__header" @click="toggleSection('highlights')">
        <span>Highlights</span>
        <svg class="tfp-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      <div v-if="highlights.length" class="tfp-summary">
        {{ highlights.join(', ') }}
        <button class="tfp-clear-chip" @click.stop="clearSection('highlights')">✕</button>
      </div>
      <div v-show="sectionsOpen.highlights" class="tfp-section__body">
        <p v-if="!highlightOptions.length" class="text-xs" style="color: var(--color-text-muted, #9ca3af);">
          No options available yet.
        </p>
        <div v-else class="space-y-2">
          <label v-for="opt in highlightOptions" :key="opt" class="tfp-checkbox-label">
            <input type="checkbox" :value="opt" v-model="highlights" class="tfp-checkbox" />
            {{ opt }}
          </label>
        </div>
      </div>
    </div>

    <!-- Mobile apply button -->
    <div class="lg:hidden p-4 border-t sticky bottom-0 bg-white" style="border-color: var(--color-border, #e5e7eb); background: var(--color-bg-panel, #ffffff);">
      <button
        class="w-full py-2 px-4 rounded-full text-sm font-medium text-white"
        style="background: var(--brand-accent);"
        @click="closeDrawer"
      >
        Show results
      </button>
    </div>
  </aside>
</template>

<script lang="ts" src="./tour_filter_panel.ts"></script>